import type { FC, ReactNode } from 'react'
import type { AttendanceStatus } from '@/types'
import type { BadgeTone } from '@/components/ui/badge'

import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { ATTENDANCE_LABELS } from '@/constants/academic'

export const STATUS_TONE: Record<AttendanceStatus, BadgeTone> = {
    H: 'success',
    S: 'warning',
    I: 'info',
    A: 'danger'
}

export const STATUS_FILL: Record<AttendanceStatus, string> = {
    H: 'bg-success',
    S: 'bg-warning',
    I: 'bg-info',
    A: 'bg-destructive'
}

export const STATUS_SOFT: Record<AttendanceStatus, string> = {
    H: 'bg-success/12 text-success',
    S: 'bg-warning/14 text-warning',
    I: 'bg-info/12 text-info',
    A: 'bg-destructive/12 text-destructive'
}

export const AttendanceBadge: FC<{ status: AttendanceStatus, className?: string }> = ({ status, className }): ReactNode => {
    return (
        <Badge tone={STATUS_TONE[status]} dot className={className}>
            {ATTENDANCE_LABELS[status]}
        </Badge>
    )
}

// Compact one-letter cell used in monthly attendance grids
export const StatusCode: FC<{ status: AttendanceStatus | null | undefined }> = ({ status }): ReactNode => {
    if (!status) {
        return <span className='text-muted-foreground/50' aria-label='Belum ada catatan'>–</span>
    }

    return (
        <span
            className={cn('inline-grid size-7 place-items-center rounded-md text-xs font-bold', STATUS_SOFT[status])}
            title={ATTENDANCE_LABELS[status]}
        >
            {status}
        </span>
    )
}

export const StatusLegend: FC<{ className?: string }> = ({ className }): ReactNode => {
    return (
        <p className={cn('flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground', className)}>
            {(['H', 'S', 'I', 'A'] as AttendanceStatus[]).map(status => (
                <span key={status} className='inline-flex items-center gap-1.5'>
                    <span className={cn('inline-grid size-5 place-items-center rounded text-[10px] font-bold', STATUS_SOFT[status])} aria-hidden='true'>
                        {status}
                    </span>

                    {ATTENDANCE_LABELS[status]}
                </span>
            ))}
        </p>
    )
}
